import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Type, Palette, ArrowUpDown, RotateCcw, Grid3x3, Move, LayoutGrid, Plus, FileText } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { DraggableTextBlock } from './DraggableTextBlock';
import {
  A4_W, A4_H, PAGE_PAD_PX, CONTENT_W, GRID,
  PAPER_FONT_OPTIONS, PAPER_ROLES, renderBlockHtml,
  computeAutoLayout, resolveLayout, buildMenuHtml,
} from '../../lib/paperMenuModel';

// Editor WYSIWYG a TELA LIBERA per la carta cartacea.
// Ogni blocco (titoli, categorie, piatti, prezzi) è posizionato in assoluto nella
// pagina A4 e può essere trascinato liberamente (drag fluido) e spostato da una
// pagina all'altra. Il trascinamento usa un overlay flottante e committa la nuova
// posizione UNA SOLA VOLTA al rilascio (nessuno scatto, nessuna ri-serializzazione live).
export const PaperMenuEditor = ({ blocks, value, onChange, onHtmlChange, accent, money, aiPageBg, width = 760, t }) => {
  const groupStyles = value?.groupStyles || {};
  const overrides = value?.overrides || {};
  const [selectedId, setSelectedId] = useState(null);
  const [editRole, setEditRole] = useState('itemName');
  const [showGrid, setShowGrid] = useState(true);
  const [hoverPage, setHoverPage] = useState(-1);
  const [draggingId, setDraggingId] = useState(null);
  const measRef = useRef(null);
  const [heights, setHeights] = useState([]);

  const overlayRef = useRef(null);
  const pageRefs = useRef([]);
  const dragRef = useRef(null);

  const tr = t || ((k, o) => (o && o.defaultValue) || k);
  const scale = width / A4_W;
  const dispH = A4_H * scale;

  const roleLabels = {
    menuTitle: tr('cartaDigitale.editor.roleMenuTitle', { defaultValue: 'Titolo menu' }),
    category: tr('cartaDigitale.editor.roleCategory', { defaultValue: 'Titoli categoria' }),
    subcategory: tr('cartaDigitale.editor.roleSubcategory', { defaultValue: 'Sottocategorie' }),
    itemName: tr('cartaDigitale.editor.roleItemName', { defaultValue: 'Nome piatto' }),
    itemPrice: tr('cartaDigitale.editor.roleItemPrice', { defaultValue: 'Prezzo' }),
    itemDesc: tr('cartaDigitale.editor.roleItemDesc', { defaultValue: 'Descrizione' }),
  };

  // HTML pre-renderizzato di ogni blocco (dipende solo dagli stili, non dalla posizione).
  const styleKey = useMemo(
    () => JSON.stringify(Object.fromEntries(Object.entries(overrides).map(([k, v]) => [k, { f: v.fontId, s: v.sizePx, c: v.color }]))),
    [overrides],
  );
  const htmls = useMemo(
    () => blocks.map((b) => renderBlockHtml(b, { groupStyles, overrides, accent, money })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [blocks, groupStyles, styleKey, accent, money],
  );
  const htmlById = useMemo(() => {
    const m = {};
    blocks.forEach((b, i) => { m[b.id] = htmls[i]; });
    return m;
  }, [blocks, htmls]);

  // Misura l'altezza reale di ogni blocco (per la disposizione automatica di default).
  useLayoutEffect(() => {
    const el = measRef.current;
    if (!el) return undefined;
    const measure = () => setHeights(Array.from(el.children).map((k) => k.getBoundingClientRect().height));
    measure();
    const id = setTimeout(measure, 300);
    return () => clearTimeout(id);
  }, [htmls]);

  // Layout risolto (posizione { page, y } per ogni blocco).
  const { auto, autoPageCount } = useMemo(() => {
    if (heights.length !== blocks.length) {
      const a = {}; blocks.forEach((b, i) => { a[b.id] = { page: 0, y: i * 40 }; });
      return { auto: a, autoPageCount: 1 };
    }
    const r = computeAutoLayout(blocks, heights, overrides);
    return { auto: r.auto, autoPageCount: r.pageCount };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heights, blocks, styleKey]);

  const layout = useMemo(() => resolveLayout(blocks, auto, overrides), [blocks, auto, overrides]);

  const maxPosPage = useMemo(
    () => blocks.reduce((mx, b) => Math.max(mx, (overrides[b.id]?.pos?.page ?? -1)), -1),
    [blocks, overrides],
  );
  const pageCount = Math.max(autoPageCount, maxPosPage + 1, value?.pages || 0, 1);

  // Emette l'HTML serializzato per PDF/stampa quando cambia layout o stili (una volta per commit).
  useEffect(() => {
    if (heights.length !== blocks.length) return;
    const html = buildMenuHtml(blocks, layout, pageCount, { groupStyles, overrides, accent, money, aiPageBg });
    if (onHtmlChange) onHtmlChange(html);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout, pageCount, groupStyles, styleKey, accent, money, aiPageBg, heights.length]);

  // ---- Mutazioni ----
  const patchGroup = (role, patch) => onChange({
    ...value,
    groupStyles: { ...groupStyles, [role]: { ...groupStyles[role], ...patch } },
  });
  const patchOverride = (id, patch) => onChange({
    ...value,
    overrides: { ...overrides, [id]: { ...(overrides[id] || {}), ...patch } },
  });
  const resetBlock = (id) => {
    const next = { ...overrides };
    delete next[id];
    onChange({ ...value, overrides: next });
    setSelectedId(null);
  };
  const resetPos = (id) => {
    const ov = { ...(overrides[id] || {}) };
    delete ov.pos;
    onChange({ ...value, overrides: { ...overrides, [id]: ov } });
  };
  const moveToPage = (id, page) => {
    const cur = layout[id] || { page: 0, y: 0 };
    patchOverride(id, { pos: { page, y: cur.y } });
  };
  const addPage = () => onChange({ ...value, pages: pageCount + 1 });

  // ---- Trascinamento fluido (overlay flottante) ----
  const pageIndexAt = useCallback((clientX, clientY) => {
    for (let i = 0; i < pageRefs.current.length; i++) {
      const el = pageRefs.current[i];
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom) return i;
    }
    return -1;
  }, []);

  const onMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d) return;
    d.moved = true;
    d.lastX = e.clientX;
    d.lastY = e.clientY;
    if (d.raf) return;
    d.raf = requestAnimationFrame(() => {
      d.raf = null;
      const ov = overlayRef.current;
      if (ov) {
        ov.style.left = `${d.lastX - d.grabX}px`;
        ov.style.top = `${d.lastY - d.grabY}px`;
      }
      const pi = pageIndexAt(d.lastX, d.lastY);
      setHoverPage((prev) => (prev === pi ? prev : pi));
    });
  }, [pageIndexAt]);

  const onUp = useCallback((e) => {
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
    const d = dragRef.current;
    dragRef.current = null;
    setHoverPage(-1);
    setDraggingId(null);
    if (d?.raf) cancelAnimationFrame(d.raf);
    const ov = overlayRef.current;
    if (ov) { ov.style.display = 'none'; ov.innerHTML = ''; }
    if (!d) return;
    if (!d.moved) return; // semplice click -> resta solo selezionato
    const blockTopX = e.clientX - d.grabX;
    const blockTopY = e.clientY - d.grabY;
    let pi = pageIndexAt(e.clientX, e.clientY);
    if (pi < 0) pi = pageIndexAt(blockTopX + 12, blockTopY + 12);
    if (pi < 0) return; // rilasciato fuori dalle pagine -> annulla
    const rect = pageRefs.current[pi].getBoundingClientRect();
    let localY = (blockTopY - rect.top) / scale - PAGE_PAD_PX;
    localY = Math.max(0, Math.round(localY / GRID) * GRID);
    const maxY = A4_H - PAGE_PAD_PX * 2;
    localY = Math.min(localY, maxY - 8);
    patchOverride(d.blockId, { pos: { page: pi, y: localY } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onMove, pageIndexAt, scale, overrides, value]);

  const onDragStart = useCallback((e, blockId, html) => {
    if (e.button != null && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    setSelectedId(blockId);
    setDraggingId(blockId);
    const rect = e.currentTarget.getBoundingClientRect();
    const ov = overlayRef.current;
    if (ov) {
      ov.style.display = 'block';
      ov.style.left = `${rect.left}px`;
      ov.style.top = `${rect.top}px`;
      ov.style.width = `${CONTENT_W * scale}px`;
      ov.innerHTML = `<div style="width:${CONTENT_W}px;transform:scale(${scale});transform-origin:top left;">${html}</div>`;
    }
    dragRef.current = { blockId, grabX: e.clientX - rect.left, grabY: e.clientY - rect.top, moved: false, raf: null };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [onMove, onUp, scale]);

  useEffect(() => () => {
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
  }, [onMove, onUp]);

  const palette = [accent, '#1a1a1a', '#4b4b4b', '#7a6f66', '#b45309', '#15803d', '#ffffff'];

  const selBlock = selectedId ? blocks.find((b) => b.id === selectedId) : null;
  const selOv = selectedId ? (overrides[selectedId] || {}) : {};
  const selRole = selBlock ? (selBlock.role === 'item' ? 'itemName' : selBlock.role) : null;
  const selResolvedFont = (selOv.fontId) || (selRole && groupStyles[selRole]?.fontId) || 'montserrat';
  const selResolvedSize = (selOv.sizePx) || (selRole && groupStyles[selRole]?.sizePx) || 15;
  const selPage = selBlock ? (layout[selBlock.id]?.page || 0) : 0;

  const groupStyle = groupStyles[editRole] || {};

  const pageStyle = aiPageBg
    ? { backgroundColor: '#ffffff', backgroundImage: `linear-gradient(rgba(255,255,255,.86),rgba(255,255,255,.86)),url(${aiPageBg})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { backgroundColor: '#ffffff' };

  const ColorSwatches = ({ value: cur, onPick }) => (
    <div className="flex items-center gap-1">
      {palette.map((c, i) => (
        <button
          key={i}
          type="button"
          onClick={() => onPick(c)}
          title={c}
          className={`h-6 w-6 rounded-md border ${cur === c ? 'ring-2 ring-primary' : ''}`}
          style={{ background: c }}
          data-testid={`paper-color-${i}`}
        />
      ))}
      <input
        type="color"
        value={/^#[0-9a-fA-F]{6}$/.test(cur || '') ? cur : '#000000'}
        onChange={(e) => onPick(e.target.value)}
        className="h-6 w-8 rounded-md border cursor-pointer p-0.5"
        data-testid="paper-color-custom"
      />
    </div>
  );

  return (
    <div className="space-y-3" data-testid="paper-menu-editor">
      {/* ---- Toolbar STILI DI GRUPPO ---- */}
      <div className="rounded-xl border bg-background p-3 space-y-2">
        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
          <LayoutGrid className="h-3.5 w-3.5" /> {tr('cartaDigitale.editor.groupTitle', { defaultValue: 'Stile globale testi' })}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <Select value={editRole} onValueChange={setEditRole}>
            <SelectTrigger className="h-8 rounded-lg w-[150px]" data-testid="paper-role-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PAPER_ROLES.map((r) => <SelectItem key={r} value={r}>{roleLabels[r]}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1.5">
            <Type className="h-3.5 w-3.5 text-muted-foreground" />
            <Select value={groupStyle.fontId} onValueChange={(v) => patchGroup(editRole, { fontId: v })}>
              <SelectTrigger className="h-8 rounded-lg w-[150px]" data-testid="paper-group-font"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAPER_FONT_OPTIONS.map((o) => <SelectItem key={o.id} value={o.id}><span style={{ fontFamily: o.family }}>{o.label}</span></SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-1.5">
            <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground" />
            <Input
              type="number" min={8} max={80}
              value={groupStyle.sizePx || 15}
              onChange={(e) => patchGroup(editRole, { sizePx: Math.max(8, Math.min(80, Number(e.target.value) || 8)) })}
              className="h-8 w-16 rounded-lg" data-testid="paper-group-size"
            />
            <span className="text-xs text-muted-foreground">px</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Palette className="h-3.5 w-3.5 text-muted-foreground" />
            <ColorSwatches value={groupStyle.color} onPick={(c) => patchGroup(editRole, { color: c })} />
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <Grid3x3 className="h-3.5 w-3.5 text-muted-foreground" />
            <Label className="text-xs text-muted-foreground">{tr('cartaDigitale.editor.grid', { defaultValue: 'Griglia' })}</Label>
            <Switch checked={showGrid} onCheckedChange={setShowGrid} data-testid="paper-grid-toggle" />
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground flex items-center gap-1">
          <Move className="h-3 w-3" /> {tr('cartaDigitale.editor.hintFree', { defaultValue: 'Trascina un testo per riposizionarlo; puoi spostarlo anche su un\'altra pagina.' })}
        </p>
      </div>

      {/* ---- Toolbar BLOCCO SELEZIONATO ---- */}
      {selBlock && (
        <div className="rounded-xl border-2 border-primary/40 bg-primary/5 p-3 space-y-2" data-testid="paper-selected-toolbar">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-primary">
              {tr('cartaDigitale.editor.selected', { defaultValue: 'Blocco selezionato' })}: {roleLabels[selRole]}
            </span>
            <Button variant="ghost" size="sm" className="h-7 rounded-lg text-xs" onClick={() => resetBlock(selBlock.id)} data-testid="paper-block-reset">
              <RotateCcw className="h-3.5 w-3.5 mr-1" /> {tr('cartaDigitale.editor.resetBlock', { defaultValue: 'Ripristina blocco' })}
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <Select value={selResolvedFont} onValueChange={(v) => patchOverride(selBlock.id, { fontId: v })}>
              <SelectTrigger className="h-8 rounded-lg w-[150px]" data-testid="paper-block-font"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAPER_FONT_OPTIONS.map((o) => <SelectItem key={o.id} value={o.id}><span style={{ fontFamily: o.family }}>{o.label}</span></SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1.5">
              <Input
                type="number" min={8} max={120}
                value={selResolvedSize}
                onChange={(e) => patchOverride(selBlock.id, { sizePx: Math.max(8, Math.min(120, Number(e.target.value) || 8)) })}
                className="h-8 w-16 rounded-lg" data-testid="paper-block-size"
              />
              <span className="text-xs text-muted-foreground">px</span>
            </div>
            <ColorSwatches value={selOv.color} onPick={(c) => patchOverride(selBlock.id, { color: c })} />
            <div className="flex items-center gap-1.5">
              <FileText className="h-3.5 w-3.5 text-muted-foreground" />
              <Label className="text-xs text-muted-foreground">{tr('cartaDigitale.editor.page', { defaultValue: 'Pagina' })}</Label>
              <Select value={String(selPage)} onValueChange={(v) => moveToPage(selBlock.id, Number(v))}>
                <SelectTrigger className="h-8 rounded-lg w-[70px]" data-testid="paper-block-page"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({ length: pageCount }).map((_, i) => <SelectItem key={i} value={String(i)}>{i + 1}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {selOv.pos ? (
              <Button variant="outline" size="sm" className="h-8 rounded-lg text-xs" onClick={() => resetPos(selBlock.id)} data-testid="paper-block-reset-pos">
                {tr('cartaDigitale.editor.resetPos', { defaultValue: 'Posizione auto' })}
              </Button>
            ) : null}
          </div>
        </div>
      )}

      {/* ---- Misuratore nascosto (per la disposizione automatica) ---- */}
      <div
        ref={measRef}
        aria-hidden
        style={{ position: 'fixed', left: -10000, top: 0, width: CONTENT_W, boxSizing: 'border-box', visibility: 'hidden', zIndex: -1 }}
      >
        {htmls.map((h, i) => <div key={blocks[i].id} dangerouslySetInnerHTML={{ __html: h }} />)}
      </div>

      {/* ---- Overlay flottante per il trascinamento ---- */}
      <div
        ref={overlayRef}
        style={{ position: 'fixed', display: 'none', zIndex: 200, pointerEvents: 'none', opacity: 0.92, filter: 'drop-shadow(0 8px 20px rgba(0,0,0,.28))' }}
      />

      {/* ---- Pagine A4 ---- */}
      <div className="space-y-3">
        {Array.from({ length: pageCount }).map((_, idx) => (
          <div
            key={idx}
            ref={(el) => { pageRefs.current[idx] = el; }}
            className={`relative mx-auto rounded-lg shadow-sm overflow-hidden transition-shadow ${hoverPage === idx ? 'ring-2 ring-primary' : 'ring-1 ring-black/10'}`}
            style={{ width, height: dispH }}
            data-testid={`paper-page-${idx}`}
          >
            <div
              onPointerDown={() => setSelectedId(null)}
              style={{
                position: 'absolute', top: 0, left: 0, width: A4_W, height: A4_H,
                transform: `scale(${scale})`, transformOrigin: 'top left',
                boxSizing: 'border-box', overflow: 'hidden',
                ...pageStyle,
              }}
            >
              {showGrid && (
                <div
                  style={{
                    position: 'absolute', inset: 0, pointerEvents: 'none',
                    backgroundImage: `linear-gradient(to right, rgba(59,130,246,.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(59,130,246,.08) 1px, transparent 1px)`,
                    backgroundSize: `${GRID}px ${GRID}px`,
                  }}
                />
              )}
              {/* Cornice margini (guida visiva) */}
              <div style={{ position: 'absolute', left: PAGE_PAD_PX, top: PAGE_PAD_PX, width: CONTENT_W, height: A4_H - PAGE_PAD_PX * 2, border: '1px dashed rgba(0,0,0,.10)', pointerEvents: 'none' }} />
              {blocks.map((b) => {
                const ov = overrides[b.id] || {};
                if (ov.hidden) return null;
                const pos = layout[b.id];
                if (!pos || (pos.page || 0) !== idx) return null;
                const isDragging = draggingId === b.id;
                return (
                  <div key={b.id} style={{ opacity: isDragging ? 0.35 : 1 }}>
                    <DraggableTextBlock
                      block={b}
                      html={htmlById[b.id]}
                      left={PAGE_PAD_PX}
                      top={PAGE_PAD_PX + Math.round(pos.y || 0)}
                      width={CONTENT_W}
                      selected={selectedId === b.id}
                      onDragStart={onDragStart}
                    />
                  </div>
                );
              })}
            </div>
            <div className="absolute top-1.5 right-2 rounded-md bg-black/55 px-1.5 py-0.5 text-[10px] font-medium text-white">
              {idx + 1} / {pageCount}
            </div>
          </div>
        ))}
        <div className="flex justify-center">
          <Button variant="outline" size="sm" className="rounded-xl" onClick={addPage} data-testid="paper-add-page">
            <Plus className="h-4 w-4 mr-1.5" /> {tr('cartaDigitale.editor.addPage', { defaultValue: 'Aggiungi pagina' })}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaperMenuEditor;
